import * as React from "react";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { MetricGrid } from "@/components/dashboard/MetricGrid";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { useAnalyticsSnapshot } from "@/hooks/queries/useAnalytics";
import { appConfig } from "@/utils/config";

interface AnalyticsSnapshotPayload {
  win_rate: number | null;
  loss_rate: number | null;
  performance: { total_return: number; annualized_return: number; trade_count: number } | null;
}

/**
 * Backend v2.1 (Sprint 4.3) `GET /api/v1/analytics` - a genuine read of
 * the persisted `AnalyticsSnapshot`, replacing the Sprint 3.1-era
 * always-`null` placeholder rendering. `AnalyticsSummaryDto.performance`
 * is itself `null` whenever the snapshot's own fail-safe path produced
 * an incomplete result - rendered honestly as unavailable, never
 * fabricated, matching backend Sprint 4.3's own "surfaced honestly
 * rather than hidden" precedent.
 */
export function PerformanceSummarySection(): React.JSX.Element {
  const analyticsQuery = useAnalyticsSnapshot(appConfig.defaultPortfolioId);

  return (
    <StatusCard title="Performance Summary">
      <QueryStateBoundary query={analyticsQuery}>
        {(payload) => {
          const snapshot = payload as unknown as AnalyticsSnapshotPayload;
          return (
            <MetricGrid
              metrics={[
                { label: "Total Return", value: snapshot.performance?.total_return ?? null },
                { label: "Annualized Return", value: snapshot.performance?.annualized_return ?? null },
                { label: "Win Rate", value: snapshot.win_rate },
                { label: "Loss Rate", value: snapshot.loss_rate },
                { label: "Trade Count", value: snapshot.performance?.trade_count ?? null },
              ]}
            />
          );
        }}
      </QueryStateBoundary>
    </StatusCard>
  );
}
